import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAppStore } from '../store/appStore';

const LegacyEventContext = createContext();

/**
 * EventContextAdapter 提供旧组件与新状态管理之间的兼容性
 */
export const EventContextAdapter = ({ children }) => {
  // 跟踪处理状态
  const isProcessingRef = useRef(false);
  const lastUpdateSourceRef = useRef(null);
  
  // 从 Zustand 获取状态
  const activeEventId = useAppStore(state => state.selection.activeEventId);
  const activeRobotId = useAppStore(state => state.selection.activeRobotId);
  const currentTime = useAppStore(state => state.videoState.currentTime);
  const isPlaying = useAppStore(state => state.videoState.isPlaying);
  const videoUrl = useAppStore(state => state.videoUrl);
  const timeframe = useAppStore(state => state.timeframe);
  
  // 模拟旧的处理状态
  const [processingUpdate, setProcessingUpdate] = useState(false);
  const [updateSource, setUpdateSource] = useState(null);
  
  // 获取 Zustand actions
  const updateVideoState = useAppStore(state => state.updateVideoState);
  const changeTimeframe = useAppStore(state => state.changeTimeframe);
  
  // 同步全局处理状态到本地状态
  useEffect(() => {
    // 忽略来自此适配器的更新
    if (lastUpdateSourceRef.current === 'legacyContext') {
      return;
    }
    
    const metadata = useAppStore.getState().metadata;
    if (metadata) {
      setProcessingUpdate(metadata.processingUpdate || false);
      setUpdateSource(metadata.updateSource);
    }
  }, [activeEventId, activeRobotId, currentTime, isPlaying, videoUrl, timeframe]);
  
  // 设置活跃事件 - 关键函数，用于处理事件点击
  const setActiveEvent = (eventId, robotId, time) => {
    // 防止循环更新
    if (isProcessingRef.current) return;
    
    try {
      // 标记为处理中
      isProcessingRef.current = true;
      lastUpdateSourceRef.current = 'legacyContext';
      
      console.log(`EventContextAdapter: 设置活跃事件 - ID: ${eventId}, 机器人: ${robotId}, 时间: ${time}`);
      
      // 检查是否需要切换视频
      const isVideoSwitch = robotId !== undefined && robotId !== activeRobotId;
      
      // 如果切换视频，更新URL
      if (isVideoSwitch && robotId) {
        const timePart = timeframe === "11AM" ? "day" : "night";
        const newVideoUrl = `/${timePart}/videos/video${robotId}.mp4`;
        
        // 暂停播放
        updateVideoState({ isPlaying: false }, 'legacyContext');
        
        // 先更新机器人ID和事件ID
        updateVideoState({
          robotId: robotId,
          eventId: eventId !== undefined ? eventId : null
        }, 'legacyContext');
        
        // 然后更新视频URL和时间
        updateVideoState({
          videoUrl: newVideoUrl,
          time: time !== undefined ? time : currentTime
        }, 'legacyContext');
        
        // 短延迟后恢复播放
        setTimeout(() => {
          updateVideoState({ isPlaying: true }, 'legacyContext');
        }, 200);
      } 
      // 如果只是时间或事件ID变化
      else {
        // 构建更新对象，只包含实际变化的值
        const updates = {};
        if (eventId !== undefined && eventId !== activeEventId) updates.eventId = eventId;
        if (robotId !== undefined && robotId !== activeRobotId) updates.robotId = robotId;
        if (time !== undefined && Math.abs(time - currentTime) > 0.1) updates.time = time;
        
        // 如果有实际更新，执行更新
        if (Object.keys(updates).length > 0) {
          console.log('EventContextAdapter: 更新状态:', updates);
          updateVideoState(updates, 'legacyContext');
        }
      }
    } finally {
      // 确保重置处理标志
      setTimeout(() => {
        isProcessingRef.current = false;
        lastUpdateSourceRef.current = null;
      }, 100);
    }
  };
  
  // 创建上下文值
  const contextValue = {
    // 状态
    activeEventId, 
    activeRobotId, 
    currentTime,
    isPlaying,
    videoUrl,
    timeframe,
    processingUpdate,
    updateSource,
    
    // 关键方法 - 设置活跃事件
    setActiveEvent,
    
    // 包装的状态更新方法
    updateVideoState: (updates, source = 'adapter') => {
      // 防止循环更新
      if (isProcessingRef.current) return;
      
      try {
        isProcessingRef.current = true;
        lastUpdateSourceRef.current = source;
        
        // 直接传递到Zustand
        updateVideoState(updates, source);
      } finally {
        setTimeout(() => {
          isProcessingRef.current = false;
          lastUpdateSourceRef.current = null;
        }, 100);
      }
    },
    
    // 时间帧变化
    changeTimeframe
  };

  return (
    <LegacyEventContext.Provider value={contextValue}>
      {children}
    </LegacyEventContext.Provider>
  );
};

// 导出获取上下文的钩子
export const useEventContext = () => useContext(LegacyEventContext);